import React, { useEffect } from 'react'
import { Column, useSortBy, useTable, SortingRule } from 'react-table'
import isFunction from 'lodash/isFunction'

type TableProps<Data extends {}> = {
  columns: Column<Data>[]
  data: Data[]
  onSortBy?: (sortBy: SortingRule<Data>[]) => void
}

const Table = <TableData extends {}>(props: TableProps<TableData>) => {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, footerGroups, state, rows } = useTable<TableData>(
    {
      columns: props.columns,
      data: props.data,
      manualSortBy: !!isFunction(props.onSortBy),
      autoResetSortBy: false,
      autoResetHiddenColumns: false,
    },
    useSortBy
  )

  const { onSortBy } = props
  const { sortBy } = state

  useEffect(() => {
    if (isFunction(onSortBy)) {
      onSortBy(sortBy)
    }
  }, [sortBy, onSortBy])

  return (
    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
      <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200" {...getTableProps()}>
            <thead className="bg-secondary-50">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th className="table-header select-none" {...column.getHeaderProps(column.getSortByToggleProps())}>
                      <span className="flex items-center">
                        {column.render('Header')}
                        <span className="text-secondary-700 inline-block w-2">
                          {column.isSorted ? (column.isSortedDesc ? '▲' : '▼') : ' '}
                        </span>
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} className="even:bg-secondary-25">
                    {row.cells.map((cell) => {
                      return (
                        <td
                          className="truncate whitespace-nowrap px-4 py-6 text-sm text-gray-500"
                          {...cell.getCellProps()}
                        >
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
            {footerGroups[0].headers.some((column) => {
              return column?.enableFooter
            }) ? (
              <tfoot>
                {footerGroups.map((group) => (
                  <tr {...group.getFooterGroupProps()}>
                    {group.headers.map((column) => (
                      <td
                        className="truncate whitespace-nowrap px-4 py-4 text-sm text-gray-500"
                        {...column.getFooterProps()}
                      >
                        {column.render('Footer')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>
    </div>
  )
}

export default React.memo(Table) as typeof Table
